import React, { useState, useEffect, useRef } from "react";
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import ExpertLogin from "./pages/ExpertLogin";
import ExpertSignup from "./pages/ExpertSignup";
import ExpertDashboard from "./pages/ExpertDashboard";

function App() {
  const [page, setPage] = useState("login");
  const [expertUser, setExpertUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // A firm guard to block auth listeners during file uploads and database writes
  const [isRegistering, setIsRegistering] = useState(false);

  // Keeps a live subscription to the logged-in expert's Firestore doc so that
  // admin changes (fee assigned, approval, deactivation, etc.) are reflected
  // immediately without requiring the expert to log out and back in.
  const expertDocUnsubRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      // If the user is registering, block the listener from redirecting completely
      if (isRegistering || page === "signup") {
        setAuthChecked(true);
        return;
      }

      // Tear down any previous expert-doc subscription before starting a new one
      if (expertDocUnsubRef.current) {
        expertDocUnsubRef.current();
        expertDocUnsubRef.current = null;
      }

      if (user) {
        // Track the last known approvalStatus so we don't call setExpertUser
        // on every Firestore delivery (stats writes, availability saves, etc.
        // all touch experts/{uid} and would otherwise pass a brand-new user
        // object to ExpertDashboard on each write, causing unnecessary re-renders).
        let lastApprovalStatus = null;

        const unsubExpertDoc = onSnapshot(
          doc(db, "experts", user.uid),
          (docSnap) => {
            // Firestore's SDK always delivers a first callback from local
            // cache before the server-confirmed value arrives. If this
            // browser session ever read this doc while the expert was still
            // "pending" (e.g. an earlier blocked login attempt), that stale
            // snapshot is what gets served here first — momentarily
            // overriding a fresh approval and signing an already-approved
            // expert straight back out. Skip cache-only snapshots and act
            // only once the server confirms it.
            if (docSnap.metadata.fromCache) {
              return;
            }

            if (!docSnap.exists()) {
              auth.signOut();
              setExpertUser(null);
              setPage("login");
              setAuthChecked(true);
              return;
            }

            const data = docSnap.data();
            const currentStatus = data.approvalStatus;

            // approvalStatus is the field the admin panel and dashboard both
            // use for approval state. Only act when it actually changes —
            // this prevents stats/availability/lastLoginAt writes from
            // causing a new expertUser object to be passed to ExpertDashboard
            // on every Firestore delivery.
            if (currentStatus === lastApprovalStatus && authChecked) {
              // Status unchanged — skip the setState to avoid re-render
              return;
            }
            lastApprovalStatus = currentStatus;

            if (currentStatus === "approved") {
              setExpertUser({ uid: user.uid, ...data });
              setPage("dashboard");
            } else {
              auth.signOut();
              setExpertUser(null);
              setPage("login");
            }
            setAuthChecked(true);
          },
          (err) => {
            console.error("Session restore error:", err);
            auth.signOut();
            setExpertUser(null);
            setPage("login");
            setAuthChecked(true);
          }
        );
        expertDocUnsubRef.current = unsubExpertDoc;
      } else {
        setExpertUser(null);
        if (page !== "signup") {
          setPage("login");
        }
        setAuthChecked(true);
      }
    });

    return () => {
      unsubscribe();
      if (expertDocUnsubRef.current) {
        expertDocUnsubRef.current();
        expertDocUnsubRef.current = null;
      }
    };
  }, [page, isRegistering]);

  const handleLoginSuccess = (userData) => {
    setExpertUser(userData);
    setPage("dashboard");
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
    } catch (err) {
      console.error("Logout error:", err);
    }
    setExpertUser(null);
    setPage("login");
  };

  if (!authChecked) {
    return (
      <div style={{
        minHeight: "100vh",
        background: "#090d16",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
        <div style={{
          width: "36px",
          height: "36px",
          border: "3px solid rgba(0, 210, 196, 0.15)",
          borderTop: "3px solid #00d2c4",
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <>
      {page === "login" && (
        <ExpertLogin
          onRegisterClick={() => setPage("signup")}
          onLoginSuccess={handleLoginSuccess}
        />
      )}

      {page === "signup" && (
        <ExpertSignup
          onLoginClick={() => setPage("login")}
          setIsRegistering={setIsRegistering} // Pass the guard handler down
        />
      )}

      {page === "dashboard" && (
        <ExpertDashboard
          user={expertUser}
          onLogout={handleLogout}
        />
      )}
    </>
  );
}

export default App;